import { Component, computed, inject, input, output, TemplateRef } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TypeAheadSelector2Component } from '../type-ahead-selector2/type-ahead-selector2.component';
import { map, Observable } from 'rxjs';
import { PricePolicyType, PricePolicy, PricePolicySearchEvent } from 'src/app/features/prices/price-policies/interfaces/price-policy';
import { PricePolicyV2Service } from '../../services/price-policy-v2.service';
import { PagedResult } from '../../models/paging';
import { PageEvent } from '@angular/material/paginator';
import { DkCurrencyPipe } from '../../pipes/dk-currency';

@Component({
  selector: 'adm-adm-price-policy-typeahead',
  imports: [TypeAheadSelector2Component, DkCurrencyPipe],
  templateUrl: './adm-price-policy-typeahead.component.html',
  styleUrl: './adm-price-policy-typeahead.component.scss',
})
export class AdmPricePolicyTypeaheadComponent {
  pricePolicyV2Service = inject(PricePolicyV2Service);
  label = input<string>('Vælg prispolitik');
  organizationId = input<number | null>(null);
  initialPricePolicyId = input<string | null>(null);
  itemTemplate = input<TemplateRef<any>>();
  itemSelected = output<PricePolicy | null>();
  resetTrigger = input<{ reset: boolean }>({ reset: false });
  includeGlobal = input<boolean>(false);
  alwaysIncludeDynamic = input<boolean>(false);
  type = input<PricePolicyType | null>(null);
  disabled = input<boolean>(false);
  required = input<boolean>(false);
  validationControl = input<AbstractControl | null>(null);
  requiredErrorText = input<string>('Prispolitik skal vælges fra listen');
  suffixIcon = input<string | null>(null);
  suffixIconTooltip = input<string | null>(null);

  fetchPricePolicies = (term: string): Observable<PricePolicy[]> => {
    const pageEvent: PageEvent = {
      pageIndex: 0,
      pageSize: 1000,
      length: 0,
    };

    const searchEvent = new PricePolicySearchEvent(pageEvent);
    searchEvent.query = term.length > 0 ? term : null;
    searchEvent.organizationId = this.organizationId();
    searchEvent.includeGlobal = this.includeGlobal();
    searchEvent.alwaysIncludeDynamic = this.alwaysIncludeDynamic();
    searchEvent.type = this.type();

    return this.pricePolicyV2Service.getPricePolicies(searchEvent).pipe(map((result: PagedResult<PricePolicy>) => result.items));
  };

  // A new function reference makes the selector refetch when a filter input changes.
  protected readonly fetchPricePoliciesForFilters = computed(() => {
    this.organizationId();
    this.includeGlobal();
    this.alwaysIncludeDynamic();
    this.type();
    return (term: string) => this.fetchPricePolicies(term);
  });

  fetchPricePolicyById = (id: string): Observable<PricePolicy | null> => {
    const pageEvent: PageEvent = {
      pageIndex: 0,
      pageSize: 25,
      length: 0,
    };

    const searchEvent = new PricePolicySearchEvent(pageEvent);
    searchEvent.pricePolicyId = parseInt(id);

    return this.pricePolicyV2Service
      .getPricePolicies(searchEvent)
      .pipe(map((result: PagedResult<PricePolicy>) => result.items.find(item => item.id === parseInt(id)) ?? null));
  };

  pricePolicySelected = (pricePolicy: PricePolicy | null) => {
    this.itemSelected.emit(pricePolicy ?? null);
  };
}
